import { Fragment, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import { Check, XCircle, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/utils/classnames";
import { useCheckDepositDrawer } from "@/hooks/invoice/use-check-deposit-drawer";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button-tw";
import { useInvoices } from "@/hooks/invoice/use-invoices";
import { Controller, useController, useForm } from "react-hook-form";
import { addresseeNamePriority } from "@/utils/invoice";
import { convertThousandSeparator } from "@/scripts/functions";
import dayjs from "dayjs";
import { useInvoice } from "@/hooks/invoice/use-invoice";
import { formatPhoneUS } from "@/utils/functions";
import { PhoneInput } from "@/components/form/phone-input";
import { CurrencyInputMasked } from "@/components/form/currency-input";
import {
  PAST_DUE_INVOICE_STATUS,
  UNPAID_AND_OVERDUE_INVOICE_STATUS,
  UNPAID_INVOICE_STATUS,
} from "@/constants/invoice";
import { useInitiateMRDC } from "@/hooks/mrdc/use-initiate-mrdc";
import { toast } from "react-toastify";
import { MobileScreenShare } from "@mui/icons-material";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import { useUpdateMRDC } from "@/hooks/mrdc/use-update-mrdc";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useProfile } from "@/hooks/profile/use-profile";
import { useSendMRDCLink } from "@/hooks/mrdc/use-send-mrdc-link";
import { useCustomCountdown } from "../../hooks/useCountDown";

// Test comment to push

function Loading() {
  return (
    <div>
      <div role="status" className="px-6 py-8 animate-pulse">
        <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[330px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[300px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px]"></div>
        <span className="sr-only">Loading...</span>
      </div>
      <div
        role="status"
        className=" p-4 space-y-4 border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse dark:divide-gray-700 md:p-6 dark:border-gray-700"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <span className="sr-only">Loading...</span>
      </div>
      <div
        role="status"
        className="absolute bottom-0 inset-x-0 p-4 space-y-4 border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse dark:divide-gray-700 md:p-6 dark:border-gray-700"
      ></div>
    </div>
  );
}

function Error() {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            There was an error loading the contact details.
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <ul className="list-disc space-y-1 pl-5">
              <li>Make sure your internet connection is stable</li>
              <li>Try closing and opening the drawer again</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CheckDepositDrawer() {
  const { mutate, isLoading: updating } = useUpdateMRDC();
  const { drawerData, open, handleClose, isLoading, isError } =
    useCheckDepositDrawer();

  const handleCancel = () => {
    if (!drawerData?.mrdc_id) return;

    mutate(
      { id: drawerData?.mrdc_id, payload: { status: "canceled" } },
      {
        onSuccess: () => {
          handleClose();
          toast.success("Check deposit canceled successfully!", {
            autoClose: 7000,
          });
        },
        onError: () => {
          toast.error(
            "It was not possible to cancel deposit. Please try again.",
            {
              autoClose: 7000,
            }
          );
        },
      }
    );
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            handleClose();
          }}
        >
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="relative pointer-events-auto w-screen max-w-2xl">
                    <div className=" pb-[120px] flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                      <div className="px-4 py-6 sm:px-6 bg-skin-primary">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-white">
                            Check deposit
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md text-gray-50 hover:text-gray-300 focus:ring-2 focus:ring-white"
                              onClick={() => {
                                handleClose();
                              }}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Main */}
                      {isLoading ? (
                        <Loading />
                      ) : isError ? (
                        <Error />
                      ) : (
                        <div>
                          {/* Header */}
                          <div>
                            <div className="flex mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                              <div className="my-6 min-w-0 w-full flex justify-between">
                                <h1 className="text-xl font-bold text-gray-900">
                                  {drawerData
                                    ? "Deposit is pending..."
                                    : "Deposit using your mobile device in just a few steps"}
                                </h1>
                                {drawerData && (
                                  <Button
                                    variant="destructive"
                                    onClick={handleCancel}
                                  >
                                    Cancel
                                    {updating && (
                                      <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                            <Stepper data={drawerData} />
                          </div>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

const formSchema = Yup.object().shape({
  invoice: Yup.object().required("Invoice is required"),
  amount: Yup.number()
    .min(1, "Check amount must be over $1.00.")
    .required("Amount is required"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Phone number must have 10 digits")
    .required("Phone number is required."),
});

function Stepper({ data }) {
  const [timeLeft, { start }] = useCustomCountdown(15000, 1000);
  const { data: profile } = useProfile();
  const { mutate: initiateMRDC, isLoading: initiating } = useInitiateMRDC();
  const { mutate: sendLink } = useSendMRDCLink();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { data: defaultInvoice } = useInvoice({
    invoiceId: queryParams.get("mrdc"),
    enabled:
      !!queryParams.get("mrdc") && !!(queryParams.get("mrdc") !== "true"),
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    getValues,
    setValue,
    setError,
    watch,
  } = useForm({
    resolver: yupResolver(formSchema),
    values: {
      invoice: defaultInvoice || null,
      amount: 0.0,
      phone: profile?.business_phone,
    },
  });

  const id = watch("invoice")?.invoice_id;
  const { data: invoice } = useInvoice({
    invoiceId: id || data?.invoice_id,
    enabled: !!id || !!data?.invoice_id,
  });

  const onSubmit = ({ amount, phone, invoice }) => {
    if (invoice.invoice_info.invoice_total !== amount) {
      setError("amount", {
        type: "manual",
        message: "Check amount must be equal to invoice total.",
      });

      return;
    }

    initiateMRDC(
      {
        invoiceId: invoice.invoice_id,
        amount: Number(amount),
        phone_number: phone,
      },
      {
        onSuccess: (data) => {
          sendLink(
            { mrdcId: data?.mrdc_id, phoneNumber: phone },
            {
              onSuccess: (data) => {
                console.log("SEND_LINK_RESPONSE::", data);
                start();
              },
              onError: (err) => {
                console.log(err);
                toast.error("Error sending link");
              },
            }
          );
          console.log("MRDC_ID::", data?.mrdc_id);
        },
        onError: (err) => {
          console.log(err);
          toast.error("Error initiating deposit");
        },
      }
    );
  };

  return (
    <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
      <ol role="list" className="overflow-hidden">
        <li className={cn("pb-10 relative")}>
          <div
            aria-hidden="true"
            className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
          />
          <div
            aria-current="step"
            className="group relative flex items-start w-full"
          >
            <span aria-hidden="true" className="flex h-9 items-center">
              <span
                className={cn(
                  "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-skin-primary bg-white",
                  {
                    "border-gray-300": data,
                  }
                )}
              >
                <span
                  className={cn("h-2.5 w-2.5 rounded-full bg-skin-primary/95", {
                    "bg-gray-300": data,
                  })}
                />
              </span>
            </span>

            <div className=" ml-4 flex flex-col grow mb-6">
              <div className="flex flex-col w-full mb-4">
                <div className="text-sm font-semibold leading-6 text-gray-900">
                  {data
                    ? "Invoice"
                    : "Select the invoice that matches the check you are depositing"}
                </div>
                {!data && (
                  <InvoiceCombobox
                    control={control}
                    name={"invoice"}
                    defaultInvoice={defaultInvoice}
                    setField={setValue}
                  />
                )}
              </div>
              {invoice && (
                <div className="rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5 pb-6">
                  <dl className="flex flex-wrap">
                    <div className="flex-auto pl-6 pt-6">
                      <dd className="mt-1 text-lg font-semibold leading-6 text-gray-900">
                        $
                        {convertThousandSeparator(
                          invoice?.invoice_info.invoice_total
                        )}
                      </dd>
                    </div>
                    <div className="flex-none self-end px-6 pt-4">
                      <dt className="sr-only">Status</dt>
                      <dd className="">
                        <InvoiceStatusBadge
                          status={invoice?.invoice_info.invoice_status}
                        />
                      </dd>
                    </div>
                    <div className="mt-6 flex w-full flex-none gap-x-4 border-t border-gray-900/5 px-6 pt-6">
                      <dd className="text-sm font-medium leading-6 text-gray-900">
                        <div>
                          {invoice?.invoice_info.invoice_to.business_name}
                        </div>
                        <div className="text-gray-400">
                          {invoice?.invoice_info.invoice_to.email}
                        </div>
                        <div className="text-gray-400">
                          {formatPhoneUS(
                            invoice?.invoice_info.invoice_to.phone
                          )}
                        </div>
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <div className="mt-4 flex flex-none gap-x-4 px-6 w-1/2">
                        <dt className="flex-none text-sm font-medium leading-6 text-gray-900">
                          Due on
                        </dt>
                        <dd className="text-sm leading-6 text-gray-500">
                          <time dateTime="2023-01-31">
                            {dayjs(
                              invoice?.invoice_info.invoice_due_date
                            ).format("LL")}
                          </time>
                        </dd>
                      </div>
                      <div className="mt-4 flex  flex-none gap-x-4 px-6">
                        <dt className="flex-none text-sm font-medium leading-6 text-gray-900">
                          Issued on
                        </dt>
                        <dd className="text-sm leading-6 text-gray-500">
                          <time dateTime="2023-01-31">
                            {dayjs(
                              invoice?.invoice_info.invoice_send_date
                            ).format("LL")}
                          </time>
                        </dd>
                      </div>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          </div>
        </li>
        <li className={cn("pb-10 relative")}>
          <div
            aria-hidden="true"
            className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
          />

          <div aria-current="step" className="group relative flex items-start">
            <span aria-hidden="true" className="flex h-9 items-center">
              <span
                className={cn(
                  "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-skin-primary bg-white",
                  {
                    "border-gray-300": data,
                  }
                )}
              >
                <span
                  className={cn("h-2.5 w-2.5 rounded-full bg-skin-primary/95", {
                    "bg-gray-300": data,
                  })}
                />
              </span>
            </span>
            <span className="ml-4 flex min-w-0 flex-col w-full mb-6">
              <span className="text-sm font-medium text-gray-900">
                {data ? "Amount" : "Enter check amount"}
              </span>
              {!data && (
                <span className="text-sm text-gray-500">
                  Chech amount must match the invoice amount. Funds are
                  generally made available 3-5 business days after deposit. For
                  deposit cut-off and more information{" "}
                  <a
                    href="https://pocketbookhelp.zendesk.com/hc/en-us/articles/31268181975319-Mobile-Check-Deposit-Guide"
                    target="_blank"
                    className="text-blue-700 hover:text-blue-700/75 hover:cursor-pointer hover:border-b border-blue-700 "
                  >
                    click here.
                  </a>
                </span>
              )}
              {data ? (
                <div className="bg-gray-50 p-4 rounded-lg mt-2 text-base font-semibold leading-6 text-gray-900">
                  ${convertThousandSeparator(data?.amount)}
                </div>
              ) : (
                <div className="mt-2">
                  <Controller
                    control={control}
                    name="amount"
                    rules={{
                      required: {
                        message: "Amount is required",
                        value: true,
                      },
                    }}
                    render={({ field }) => (
                      <CurrencyInputMasked
                        id="amount"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              )}
              {errors.amount && (
                <p role="alert" className="text-sm text-red-600 mt-1">
                  {errors.amount.message}
                </p>
              )}
            </span>
          </div>
        </li>

        <li className={cn("relative pb-20")}>
          <div
            aria-hidden="true"
            className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
          />

          <div className="group relative flex items-start">
            <span aria-hidden="true" className="flex h-9 items-center">
              <span
                className={cn(
                  "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-skin-primary bg-white",
                  {
                    "border-gray-300": data,
                  }
                )}
              >
                <span
                  className={cn("h-2.5 w-2.5 rounded-full bg-skin-primary/95", {
                    "bg-gray-300": data,
                  })}
                />
              </span>
            </span>
            <span className="ml-4 flex min-w-0 w-full flex-col gap-y-1 mb-6">
              <span className="text-sm font-medium text-gray-900">
                {data ? "Phone" : "Enter phone number"}
              </span>
              {data ? (
                <div className="bg-gray-50 p-4 rounded-lg mt-2 text-base font-semibold leading-6 text-gray-900">
                  +1 {formatPhoneUS(data?.phone_number)}
                </div>
              ) : (
                <div className="">
                  <Controller
                    control={control}
                    name="phone"
                    rules={{
                      required: {
                        message: "Phone is required",
                        value: true,
                      },
                      // pattern: {
                      //   message: "Phone number must have 9 digits",
                      //   value:
                      //     /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      // },
                    }}
                    render={({ field }) => (
                      <PhoneInput
                        id="phone"
                        autoComplete="phone"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              )}
              {errors.phone && (
                <p role="alert" className="text-sm text-red-600 mt-1">
                  {errors.phone.message}
                </p>
              )}
            </span>
          </div>
        </li>
        <li
          className={cn("relative", {
            data: "pb-20",
          })}
        >
          {data && (
            <div
              aria-hidden="true"
              className={cn(
                "absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-cyan-600",
                {
                  "bg-gray-300": data?.status === "submitted",
                }
              )}
            />
          )}
          <div className="group relative flex items-start">
            <span aria-hidden="true" className="flex h-9 items-center">
              <span
                className={cn(
                  "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-skin-primary bg-white",
                  {
                    "border-gray-300": data,
                  }
                )}
              >
                <span
                  className={cn("h-2.5 w-2.5 rounded-full bg-skin-primary/95", {
                    "bg-gray-300": data?.status === "submitted",
                  })}
                />
              </span>
            </span>
            <span className="ml-4 flex min-w-0 flex-col gap-y-1 mb-10 w-full">
              {data ? (
                <>
                  <span className="text-sm font-medium text-gray-900">
                    Check your messages
                  </span>
                  <span className="text-sm text-gray-500">
                    We have sent you an SMS containing the link for the check
                    deposit
                  </span>
                </>
              ) : (
                <>
                  <span className="text-sm font-medium text-gray-900">
                    Click 'Send SMS' below to take photos of the check and
                    submit your deposit
                  </span>
                  <span className="text-sm text-gray-500">
                    Double check that the invoice selected matches the check. If
                    you need to change it after the text message has been sent,
                    you will need to cancel this deposit and start over.
                  </span>
                </>
              )}

              <div className="mt-2 w-full">
                {data ? (
                  <p className="bg-gray-50 w-full p-4 rounded-lg mt-2 text-sm/6 text-gray-500">
                    Did not receive the SMS?{" "}
                    {timeLeft > 0 ? (
                      <span className="text-gray-700 font-medium">
                        {timeLeft / 1000}
                      </span>
                    ) : (
                      <button
                        type="button"
                        variant="outline"
                        disabled={initiating}
                        onClick={() => {
                          sendLink(
                            {
                              mrdcId: data?.mrdc_id,
                              phoneNumber: getValues().phone,
                            },
                            {
                              onSuccess: (data) => {
                                console.log("RESEND_LINK_RESPONSE::", data);
                              },
                              onError: (err) => {
                                console.log(err);
                                toast.error("Error sending link");
                              },
                            }
                          );
                        }}
                        className="font-semibold text-skin-primary hover:text-skin-primary/90"
                      >
                        Resend
                      </button>
                    )}
                  </p>
                ) : (
                  <Button type="submit" disabled={initiating}>
                    Send SMS{" "}
                    {initiating && (
                      <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                    )}
                  </Button>
                )}
              </div>
            </span>
          </div>
        </li>
        {data && (
          <li className={cn("relative")}>
            <div className="group relative flex items-start">
              {data?.status === "submitted" ? (
                <span aria-hidden="true" className="flex h-9 items-center">
                  <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-emerald-600 bg-white">
                    <Check className="h-2.5 w-2.5 text-emerald-600" />
                  </span>
                </span>
              ) : (
                <span aria-hidden="true" className="flex h-9 items-center">
                  <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-cyan-600 bg-white">
                    <span className="h-2.5 w-2.5 rounded-full bg-cyan-600 animate-ping" />
                  </span>
                </span>
              )}
              <div className="ml-4 flex min-w-0 w-full flex-col gap-y-1 ">
                <div className=" flex justify-start items-center relative">
                  <div className="relative  rounded-lg p-4 bg-gray-50 flex gap-x-2">
                    <MobileScreenShare className="w-6 h-6" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 ">
                        Submit check images to complete your deposit
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Make sure to capture good quality pictures to improve
                        the chance of approval
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </li>
        )}
      </ol>
    </form>
  );
}

function makeLabel({ invoice_info }) {
  const {
    invoice_number: invNumber,
    invoice_total: total,
    invoice_due_date: due,
  } = invoice_info;
  return `#${invNumber} - ${addresseeNamePriority({
    invoice_info,
  })} - $${convertThousandSeparator(total)} - ${dayjs(due).format(
    "MM/DD/YYYY"
  )}`;
}

function filterInvoices(invoices) {
  return invoices.filter(({ invoice_info }) =>
    [
      UNPAID_INVOICE_STATUS,
      UNPAID_AND_OVERDUE_INVOICE_STATUS,
      PAST_DUE_INVOICE_STATUS,
    ].includes(invoice_info.invoice_status)
  );
}

export function InvoiceCombobox({ control, name, setField, defaultInvoice }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(
    defaultInvoice
      ? {
          label: makeLabel(defaultInvoice),
          value: `${defaultInvoice?.invoice_info.invoice_number || ""} ${
            defaultInvoice?.invoice_info.invoice_to.business_dba || ""
          } ${defaultInvoice?.invoice_info.invoice_to.business_name || ""} ${
            defaultInvoice?.invoice_info.invoice_to.first_name || ""
          } ${defaultInvoice?.invoice_info.invoice_to.last_name || ""} ${
            defaultInvoice?.invoice_info.invoice_to.email || ""
          } ${defaultInvoice?.invoice_info.invoice_total} ${
            defaultInvoice?.invoice_info.invoice_due_date || ""
          }`,
          invoice: defaultInvoice,
        }
      : null
  );
  const { data } = useInvoices({
    select: filterInvoices,
  });
  const { field } = useController({
    control,
    name,
  });

  const invoices = useMemo(() => {
    return data?.map((invoice) => ({
      label: makeLabel(invoice),
      value: `${invoice.invoice_info.invoice_number || ""} ${
        invoice.invoice_info.invoice_to.business_dba || ""
      } ${invoice.invoice_info.invoice_to.business_name || ""} ${
        invoice.invoice_info.invoice_to.first_name || ""
      } ${invoice.invoice_info.invoice_to.last_name || ""} ${
        invoice.invoice_info.invoice_to.email || ""
      } ${invoice.invoice_info.invoice_total} ${
        invoice.invoice_info.invoice_due_date || ""
      }`,
      invoice,
    }));
  }, [data]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value ? value.label : "Select invoice..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[516px] p-0">
        <Command>
          <CommandInput placeholder="Search invoice..." />
          <CommandList>
            <CommandEmpty>No invoice found.</CommandEmpty>
            <CommandGroup>
              {invoices?.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={(currentValue) => {
                    setValue(currentValue === item.value ? null : item);
                    setField(
                      "invoice",
                      currentValue === item.value ? "" : item.invoice
                    );
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === item.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
